// src/components/TestEnvironmentBanner.tsx
// =============================================================================
// TEST ENVIRONMENT BANNER
// Shows a persistent watermark when REACT_APP_DEV_MODE=true
// Add to App.tsx to display across all pages
// =============================================================================

import React from "react";
import { Box, Typography } from "@mui/material";
import { styled, keyframes } from "@mui/material/styles";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

// Check environment variable
const IS_DEV_MODE = process.env.REACT_APP_DEV_MODE === "TRUE";

// Subtle pulse animation for the icon
const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.7; }
`;

// Fixed banner at top of screen
const Banner = styled(Box)(({ theme }) => ({
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  backgroundColor: "#ff6b00", // Bright orange
  color: "#ffffff",
  padding: theme.spacing(1, 2),
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: theme.spacing(1.5),
  zIndex: 99999, // Above everything
  boxShadow: "0 2px 8px rgba(255, 107, 0, 0.4)",
  fontFamily: "monospace",
}));

const WarningIcon = styled(WarningAmberIcon)({
  animation: `${pulse} 2s ease-in-out infinite`,
  fontSize: 20,
});

const BannerText = styled(Typography)({
  fontWeight: 700,
  fontSize: "0.875rem",
  letterSpacing: "2px",
  textTransform: "uppercase",
});

// Spacer to push content below the banner
const BannerSpacer = styled(Box)({
  height: 40, // Match banner height
});

/**
 * TestEnvironmentBanner Component
 *
 * Displays a fixed banner at the top of the screen when in development/test mode.
 * Controlled by REACT_APP_DEV_MODE environment variable.
 *
 * Usage in App.tsx:
 * ```tsx
 * import TestEnvironmentBanner from "./components/TestEnvironmentBanner";
 *
 * const App: React.FC = () => {
 *   return (
 *     <>
 *       <TestEnvironmentBanner />
 *       <ThemeProvider theme={currentTheme}>
 *         ...
 *       </ThemeProvider>
 *     </>
 *   );
 * };
 * ```
 */
const TestEnvironmentBanner: React.FC = () => {
  // Don't render anything in production
  if (!IS_DEV_MODE) {
    return null;
  }

  return (
    <>
      <Banner>
        <WarningIcon />
        <BannerText>⚠️ TEST ENVIRONMENT ⚠️</BannerText>
        <WarningIcon />
      </Banner>
      <BannerSpacer />
    </>
  );
};

export default TestEnvironmentBanner;
